/**
 * rings-color — pure color math for the three-ring meter renderer.
 *
 * Carved out of `rings.ts` per AH-W5-a.2 / FA-1 §3.3: the meter's
 * color primitives (hex parse / rgba expansion / mid-blend / phase
 * ladder derivation / cross-fade) carry no canvas dependency, so
 * they belong on their own surface where they can be directly
 * unit-tested.
 *
 * The 3-shade ladder is DERIVED from the canon — each phase's middle
 * shade is `METRIC_DEFINITIONS[phase].color` (the JS-side mirror of
 * the `--phase-{phase}` token at `styles/tokens.css`); the light
 * shade blends toward white, the dark shade toward black. There is
 * no CSS ladder token set — the canvas rings can never drift from a
 * phase's chart hue or its WCAG label companion because all three
 * forms resolve from the one canon.
 *
 * AV.W8.c / L4 — the ladder's linear blend (`mixHex`) is computed by
 * value.js `mixColors` in the byte-equivalent `"rgb"` space (the web's 8-bit
 * sRGB default), not a hand-rolled per-channel `Math.round` lerp. The output
 * is byte-identical to the retired arithmetic across the full channel grid (see
 * `docs/tranches/AV/ddr/DDR-AV-W8-BLEND-SPACE.md`), so every ladder rung and
 * cross-fade stop renders unchanged.
 *
 * AV.W11 — value.js is now a DECLARED direct dependency (transitive → direct),
 * so this `@mkbabb/value.js` import is no longer phantom-coupled through
 * keyframes/glass-ui. The old `blendHex` wrapper is retired for `mixHex` — same
 * `mixColors`-backed `"rgb"` blend (W8's DDR-AV-W8-BLEND-SPACE space choice is
 * inherited verbatim; hexes do NOT shift), just no longer named for the
 * hand-rolled lerp it replaced.
 *
 * AV.W11b.a — the BLEND is value.js (`mixColors`); the PARSE stays local by
 * necessity, not by appetite. value.js's hex→rgb primitive `hex2rgb` is NOT
 * barrel-exported at 0.11.2 (it lives at the deep `units/color/conversions/hex`
 * path — verified against the installed d.ts + an empirical import probe; a
 * deep-path import is forbidden by inv-16), and the public `parseCSSColor`
 * string parser is the wrong tool for this strict gate: it ACCEPTS 3-digit
 * shorthand (`#fff` → opaque white, where we require a length-6 reject) and
 * THROWS on malformed input (where we return null to fall through to a safe
 * default). So `parseHex` is the canonical local malformed-gate; the
 * value.js `hex2rgb` barrel re-export is a ROUTED ask (ADOPTION-ASKS.staged.md,
 * value.js §) and `hexToRgba` now DELEGATES to `parseHex` (no second bit-shift
 * copy).
 * DDR-AV-W11B-SUFFUSION §Color records the why. There is zero per-frame
 * allocation in this module's hot path: `parseHex` returns a plain tuple and
 * `mixHex` allocates the two `RGBColor` operands `mixColors` requires — the
 * ladders are precomputed at `createRings` build time and the cross-fade rides
 * the same byte-equivalent `"rgb"` mix, measured allocation-flat (perf gate in
 * the rings-color test).
 */

import { RGBColor, mixColors } from "@mkbabb/value.js";

/**
 * Phase identity for the active-ring colour stops + ladder map.
 *
 * `complete` is deliberately NOT a phase — the post-complete hold
 * paints all three named phases simultaneously via `drawAllRings`,
 * not as its own phase identity.
 */
export type PhaseKey = "ping" | "download" | "upload";

/**
 * Parse a 6-digit hex string into an RGB triple. Returns null on
 * malformed input so the caller can fall through to a safe default.
 *
 * AV.W11b.a — the strict-hex malformed gate (length-6 or reject; the
 * value.js `parseCSSColor` permissive parser accepts `#fff` shorthand and
 * throws on garbage, so it cannot stand in here — see the module header +
 * DDR-AV-W11B-SUFFUSION §Color). The value.js `hex2rgb` primitive that WOULD
 * own this is unbarreled at 0.11.2 → ROUTED ask, not a deep-path import. The
 * single source of hex→rgb truth in this module: `hexToRgba` and `mixHex`
 * both consume it (no second bit-shift copy).
 */
export function parseHex(hex: string): [number, number, number] | null {
    const clean = hex.replace("#", "").trim();
    if (clean.length !== 6) return null;
    const n = parseInt(clean, 16);
    if (Number.isNaN(n)) return null;
    return [(n >> 16) & 0xff, (n >> 8) & 0xff, n & 0xff];
}

/**
 * Pre-multiplied rgba string from a 6-digit hex colour. Used at the
 * shadow + low-alpha bloom sites where the canvas API wants a CSS
 * colour string, not a gradient.
 *
 * AV.W11b.a — delegates parse to `parseHex` (the one hex→rgb gate above),
 * retiring the inline bit-shift duplicate. Falls through to black-alpha on
 * a malformed hex, byte-identical to the retired inline form.
 */
export function hexToRgba(hex: string, alpha: number): string {
    const rgb = parseHex(hex);
    if (!rgb) return `rgba(0,0,0,${alpha})`;
    return `rgba(${rgb[0]},${rgb[1]},${rgb[2]},${alpha})`;
}

/** One 8-bit channel as a two-digit lowercase hex byte (rounded). */
function channelByte(value: number): string {
    return Math.round(value).toString(16).padStart(2, "0");
}

/**
 * Mix a base hex colour toward an accent hex colour by
 * `accentWeight ∈ [0, 1]` (clamped). Returns the mixed hex.
 *
 * The blend is value.js `mixColors` in the `"rgb"` space — the web's 8-bit
 * sRGB default (NOT the gamma-linear `"srgb-linear"`, NOT value.js's
 * perceptual `"oklab"` default). In that space the mix is a straight
 * per-channel weighted average, so this is byte-identical to the retired
 * hand-rolled `Math.round(a·(1−w) + b·w)` blend across the full channel grid
 * (DDR-AV-W8-BLEND-SPACE). The local `parseHex` is kept as the parse +
 * malformed-input gate: when either operand fails to parse we fall through to
 * `return base` unchanged (value.js exposes no public hex-parse helper).
 *
 * Used at two sites:
 *   1. `phaseLadder` — to derive the light + dark rungs from the
 *      canon mid (base → white, base → black).
 *   2. `crossfadeLadder` — to interpolate one ladder rung between
 *      two phases during the W5-L2 boundary handoff.
 */
export function mixHex(
    base: string,
    accent: string,
    accentWeight: number,
): string {
    const baseRgb = parseHex(base);
    const accentRgb = parseHex(accent);
    if (!baseRgb || !accentRgb) return base;

    const weight = Math.min(1, Math.max(0, accentWeight));
    const mixed = mixColors(
        new RGBColor(baseRgb[0], baseRgb[1], baseRgb[2]),
        new RGBColor(accentRgb[0], accentRgb[1], accentRgb[2]),
        1 - weight,
        weight,
        "rgb",
    );

    return `#${channelByte(mixed.r)}${channelByte(mixed.g)}${channelByte(mixed.b)}`;
}

/**
 * The light + dark blend weights of the ring ladder, surfaced as
 * named constants alongside the motion canon's `PHASE_HANDOFF_MS` /
 * `MILESTONE_BLOOM_MS`. Tuned so the ladder reads as one hue under
 * instrument-panel light — a restrained ±tone spread, not a rainbow.
 */
export const LADDER_LIGHTEN = 0.34;
export const LADDER_DARKEN = 0.36;

/**
 * Derive a phase's monochrome 3-shade ring ladder from its canon base.
 *
 * `base` is `METRIC_DEFINITIONS[phase].color`. The middle rung is ALWAYS
 * the base verbatim (the canon ring-0 hue — `#CC2233` for download — so the
 * dial's protagonist arc reads at full chart saturation in both schemes).
 *
 * The spread rungs are SCHEME-AWARE, mirroring the dashboard's own
 * dark-label canon (`src/design/tokens.css` `.dark` block): "light mode
 * darkens the base for contrast on a LIGHT background; dark mode lightens it
 * (toward white) for contrast on the DARK background." Both forms derive
 * from the one canon base, so the canon stays the single source of truth.
 *
 * - LIGHT (`isDark === false`, the default): `[base→white, base, base→black]`
 *   — the toward-black inner rung adds depth against the light ground.
 *   BYTE-IDENTICAL to the pre-W18 ladder, so every parity / rings test holds.
 *
 * - DARK (`isDark === true`): `[base→white, base, base→white(deeper)]` — the
 *   inner rung flips its blend TARGET from black to white at the SAME
 *   `LADDER_DARKEN` weight. This is the DESIGN-INTAKE-2 line-54 P0 fix: the
 *   prior mode-independent `base→black 36%` inner rung washed to grey/near-
 *   black against the dark ground, so the live meter read as the LEAST
 *   colourful surface in dark while the static recaps popped. Lightening
 *   toward white (the dark-ground contrast direction) keeps all three rungs
 *   in the saturated, legible band — the active phase hue now reads at the
 *   stat-card saturation the dashboard already achieves, and the phase ladder
 *   never blends toward black-illegibility. Scoped to the dark register only;
 *   no palette reauthor, no light-register change. Pairs with W14's
 *   pulse-bloom hue (W18 owns the steady-state ring chroma in dark).
 */
export function phaseLadder(
    base: string,
    isDark = false,
): [string, string, string] {
    return [
        mixHex(base, "#ffffff", LADDER_LIGHTEN),
        base,
        mixHex(base, isDark ? "#ffffff" : "#000000", LADDER_DARKEN),
    ];
}

/**
 * W5-L2 — interpolate a phase's 3-shade ladder toward another phase's
 * ladder at a cross-fade weight `t ∈ [0, 1]`.
 *
 * Before W5 the canvas dial rings HARD-CUT their colour at a phase
 * boundary while the CSS surfaces (`--phase-color`) cross-faded — the
 * rings caught the eye as the jerkiest channel (A2 §3.5). This blends
 * each rung with the shared `mixHex` helper so the rings cross-fade
 * in lockstep with the CSS phase cascade, both driven by W5-L1's
 * `usePhaseTransition` clock.
 */
export function crossfadeLadder(
    from: [string, string, string],
    to: [string, string, string],
    t: number,
): [string, string, string] {
    const w = Math.min(1, Math.max(0, t));
    return [
        mixHex(from[0], to[0], w),
        mixHex(from[1], to[1], w),
        mixHex(from[2], to[2], w),
    ];
}
